import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Button, cn } from "@win-reality/react-ui";
import { CSSProperties, forwardRef, ReactNode } from "react";

import { TeamSide } from "../../constants/teams";
import { LineUpPlayer } from "../../types/gameFull";
import { TeamRoster } from "../../types/roster";
import { formatPlayerName } from "../../utils/playerName";
import { canEditRoster, RosterEditTooltip } from "./RosterEditTooltip";

/**
 * Batting order and bench rows for the lineup management panel. Mirrors the
 * EventForm LineupList (same @dnd-kit setup, handle-only drag) with jersey
 * numbers, the Active tag, team-colored slots and TaggingV2 theme colors.
 */

export const PlayerName = ({
  player,
  "data-testid": testId,
}: {
  player: LineUpPlayer;
  /** Literal id from the call site; see TEST_IDS.md */
  "data-testid"?: string;
}) => (
  <span
    data-testid={testId}
    data-player-id={player.playerId}
    data-value={player.name}
    className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-white"
  >
    {player.jerseyNumber && (
      <span className="mr-3 text-tagging-muted">#{player.jerseyNumber}</span>
    )}
    {formatPlayerName(player.name)}
  </span>
);

type RowShellProps = {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  /** Literal id from the call site; see TEST_IDS.md */
  "data-testid"?: string;
  "data-player-id"?: string;
  "data-slot"?: number;
  "aria-current"?: "true";
};

export const RowShell = forwardRef<HTMLDivElement, RowShellProps>(
  (
    {
      children,
      style,
      className,
      "data-testid": testId,
      "data-player-id": playerId,
      "data-slot": slot,
      "aria-current": ariaCurrent,
    },
    ref,
  ) => (
    <div
      ref={ref}
      style={style}
      data-testid={testId}
      data-player-id={playerId}
      data-slot={slot}
      aria-current={ariaCurrent}
      className={cn(
        // Size
        "min-h-12 w-full",
        // Layout
        "flex items-center gap-3",
        // Spacing
        "px-3 py-2",
        // Border
        "rounded-md border border-tagging-option-border",
        // Background
        "bg-tagging-option",
        // Effects
        "transition-shadow",
        className,
      )}
    >
      {children}
    </div>
  ),
);
RowShell.displayName = "RowShell";

export const SlotBadge = ({
  children,
  color,
  textColor,
}: {
  children: ReactNode;
  color?: string;
  textColor?: string;
}) => (
  <div
    className={cn(
      // Size
      "h-6 w-6 shrink-0",
      // Layout
      "flex items-center justify-center",
      // Border
      "rounded-[3px]",
      // Background
      !color && "bg-tagging-hotkey",
      // Text
      "text-xs font-bold",
      !color && "text-tagging-muted",
    )}
    style={color ? { backgroundColor: color, color: textColor } : undefined}
  >
    {children}
  </div>
);

type SortableBattingRowProps = {
  player: LineUpPlayer;
  slot: number;
  teamColor: string;
  teamTextColor: string;
  isActive: boolean;
  isSortable: boolean;
  /** Omit to hide the remove button */
  onRemove?: () => void;
};

const SortableBattingRow = ({
  player,
  slot,
  teamColor,
  teamTextColor,
  isActive,
  isSortable,
  onRemove,
}: SortableBattingRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: player.playerId, disabled: !isSortable });

  return (
    <RowShell
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid="lineup-batting-order-item"
      data-player-id={player.playerId}
      data-slot={slot}
      aria-current={isActive ? "true" : undefined}
      className={cn(
        // Same fill as the Edit Game State modal fields in dark mode
        "dark:bg-white/[0.04]",
        isDragging && "relative z-50 shadow-lg",
      )}
    >
      {isSortable && (
        <Button
          {...attributes}
          {...listeners}
          variant="ghost"
          size="icon-sm"
          aria-label="Drag to reorder"
          data-testid="lineup-player-drag-handle"
          data-player-id={player.playerId}
          className="cursor-grab touch-none text-tagging-accent active:cursor-grabbing"
        >
          <Bars3Icon className="h-5 w-5" />
        </Button>
      )}
      <SlotBadge color={teamColor} textColor={teamTextColor}>
        {slot}
      </SlotBadge>
      <PlayerName player={player} data-testid="lineup-player-value" />
      {isActive && (
        <span
          data-testid="lineup-active-badge"
          className="text-sm font-semibold text-green-500"
        >
          Active
        </span>
      )}
      {onRemove && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onRemove}
          aria-label={`Remove ${formatPlayerName(player.name)} from the batting order`}
          data-testid="lineup-remove-player-button"
          data-player-id={player.playerId}
          className="text-tagging-muted hover:text-gray-900 dark:hover:text-white"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
      )}
    </RowShell>
  );
};

type BattingOrderListProps = {
  players: LineUpPlayer[];
  teamColor: string;
  teamTextColor: string;
  activePlayerId?: string;
  /** Omit to disable drag-to-reorder */
  onReorder?: (players: LineUpPlayer[]) => void;
  /** Omit to hide the remove buttons */
  onRemove?: (player: LineUpPlayer) => void;
  /** Which lineup this is, for tests (`data-team`) */
  "data-team"?: TeamSide;
};

export const BattingOrderList = ({
  players,
  teamColor,
  teamTextColor,
  activePlayerId,
  onReorder,
  onRemove,
  "data-team": team,
}: BattingOrderListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!onReorder || !over || active.id === over.id) return;
    const oldIndex = players.findIndex((p) => p.playerId === active.id);
    const newIndex = players.findIndex((p) => p.playerId === over.id);
    onReorder(arrayMove(players, oldIndex, newIndex));
  };

  return (
    <div
      data-testid="lineup-batting-order-list"
      data-team={team}
      className="space-y-2"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={players.map((p) => p.playerId)}
          strategy={verticalListSortingStrategy}
        >
          {players.map((player, index) => (
            <SortableBattingRow
              key={player.playerId}
              player={player}
              slot={index + 1}
              teamColor={teamColor}
              teamTextColor={teamTextColor}
              isActive={player.playerId === activePlayerId}
              isSortable={Boolean(onReorder)}
              onRemove={onRemove && (() => onRemove(player))}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

/** Bench / other-player row with an "Add to Lineup" action. */
export const AddablePlayerRow = ({
  player,
  onAdd,
  roster,
  "data-testid": testId,
}: {
  player: LineUpPlayer;
  onAdd: () => void;
  /** The roster adding joins them to: disabled (with why) if it's locked */
  roster?: TeamRoster;
  /** Literal id for the row from the call site; see TEST_IDS.md */
  "data-testid"?: string;
}) => {
  const button = (
    <Button
      variant="ghost"
      size="xs"
      isDisabled={roster && !canEditRoster(roster)}
      onClick={onAdd}
      data-testid="lineup-add-to-batting-order-button"
      data-player-id={player.playerId}
      className="shrink-0 px-2 text-sm text-tagging-accent hover:underline"
    >
      Add to Lineup
    </Button>
  );
  return (
    <RowShell data-testid={testId} data-player-id={player.playerId}>
      <SlotBadge>--</SlotBadge>
      <PlayerName player={player} data-testid="lineup-player-value" />
      {roster ? (
        <RosterEditTooltip roster={roster} className="shrink-0">
          {button}
        </RosterEditTooltip>
      ) : (
        button
      )}
    </RowShell>
  );
};
